import React, { useState } from "react";
import { View, StyleSheet, Pressable, Linking, TextInput, Platform } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Skeleton } from "@/components/ui/Skeleton";

export type EmailData = {
  name: string;
  address: string;
};

export interface EmailEditorProps {
  emails: EmailData[];
  onChange?: (emails: EmailData[]) => void;
  editing?: boolean;
  loading?: boolean;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function EmailEditor({ emails, onChange, editing = false, loading = false }: EmailEditorProps) {
  const { colors } = useTheme();
  const [validationErrors, setValidationErrors] = useState<Record<number, string>>({});
  const disabled = loading || !editing;

  const handleEmailUpdate = (index: number, field: keyof EmailData, value: string) => {
    if (!editing || !onChange) return;

    const updatedEmails = [...emails];

    if (field === "address") {
      // Strip all whitespace from email addresses
      const cleanValue = value.replace(/\s/g, "");
      const isValid = EMAIL_REGEX.test(cleanValue);

      if (!isValid && cleanValue.length > 0) {
        setValidationErrors({ ...validationErrors, [index]: "Invalid email address" });
      } else {
        const newErrors = { ...validationErrors };
        delete newErrors[index];
        setValidationErrors(newErrors);
      }

      updatedEmails[index] = { ...updatedEmails[index], address: cleanValue };
    } else {
      // Name field - allow any text
      updatedEmails[index] = { ...updatedEmails[index], name: value };
    }

    onChange(updatedEmails);
  };

  const handleRemove = (index: number) => {
    if (!editing || !onChange) return;
    const newEmails = emails.filter((_, i) => i !== index);
    const newErrors = { ...validationErrors };
    delete newErrors[index];
    setValidationErrors(newErrors);
    onChange(newEmails);
  };

  const handleEmailPress = (address: string) => {
    if (editing || !address) return;
    Linking.openURL(`mailto:${address}`);
  };

  if (loading) {
    return (
      <View style={styles.container}>
        <View style={styles.emailRow}>
          <Skeleton width={80} height={40} />
          <Skeleton width="100%" height={40} style={{ flex: 1 }} />
        </View>
      </View>
    );
  }

  if (!emails || emails.length === 0) {
    return <ThemedText style={{ opacity: 0.5 }}>No emails</ThemedText>;
  }

  return (
    <View style={styles.container}>
      {emails.map((email, index) => (
        <View key={index}>
          <View style={styles.emailRow}>
            {/* Name - Badge in view mode, text input in edit mode */}
            {editing ? (
              <TextInput
                value={email.name}
                onChangeText={(value) => handleEmailUpdate(index, "name", value)}
                placeholder="Label"
                placeholderTextColor={colors.placeholder}
                style={[styles.input, styles.nameInput, { color: colors.text, borderColor: colors.border }]}
                editable={!disabled}
              />
            ) : (
              <View style={[styles.nameBadge, { backgroundColor: colors.primary }]}>
                <MaterialCommunityIcons name="email-outline" size={14} color="white" />
                <ThemedText style={styles.nameBadgeText}>{email.name || "Email"}</ThemedText>
              </View>
            )}

            {/* Email address */}
            {editing ? (
              <TextInput
                value={email.address}
                onChangeText={(value) => handleEmailUpdate(index, "address", value.replace(/\s/g, "").trim())}
                onKeyPress={(e: any) => {
                  if (e.nativeEvent.key === " ") {
                    e.preventDefault?.();
                  }
                }}
                onBlur={() => handleEmailUpdate(index, "address", email.address.trim())}
                placeholder={disabled ? "—" : "email@example.com"}
                placeholderTextColor={colors.placeholder}
                keyboardType="email-address"
                autoCapitalize="none"
                autoCorrect={false}
                style={[styles.input, styles.addressInput, { color: colors.text, borderColor: colors.border }]}
                editable={!disabled}
              />
            ) : (
              <Pressable
                onPress={() => handleEmailPress(email.address)}
                style={[styles.viewAddressContainer, { borderColor: colors.border }]}
              >
                <ThemedText style={[styles.emailLink, { color: colors.primary }]}>{email.address || "—"}</ThemedText>
              </Pressable>
            )}

            {/* Action button - Remove in edit mode only */}
            {editing && (
              <Pressable
                onPress={() => handleRemove(index)}
                style={[styles.actionButton, { backgroundColor: colors.dangerBackground }]}
              >
                <MaterialCommunityIcons name="close" size={18} color={colors.dangerText} />
              </Pressable>
            )}
          </View>
          {validationErrors[index] && (
            <ThemedText style={[styles.errorText, { color: colors.dangerText }]}>{validationErrors[index]}</ThemedText>
          )}
        </View>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 8,
  },
  emailRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  input: {
    height: 40,
    padding: 8,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
    fontSize: 16,
    ...(Platform.OS === "web" ? { outlineStyle: "none" } : {}),
  },
  nameInput: {
    width: 140,
  },
  nameBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 6,
    minWidth: 80,
    justifyContent: "center",
  },
  nameBadgeText: {
    color: "white",
    fontSize: 13,
    fontWeight: "600",
  },
  addressInput: {
    flex: 1,
  },
  viewAddressContainer: {
    flex: 1,
    height: 40,
    paddingHorizontal: 8,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
    justifyContent: "center",
  },
  emailLink: {
    fontSize: 15,
    fontWeight: "500",
  },
  actionButton: {
    width: 36,
    height: 36,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  errorText: {
    fontSize: 12,
    marginTop: 2,
    marginLeft: 108, // Align with address input
  },
});
